/**
 * CommentForm component for adding/editing comments
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useCreateComment, useUpdateComment } from '../../services/social';
import { Loader2, Send } from 'lucide-react';
import type { Comment } from '../../types';

interface CommentFormProps {
  photoId: string;
  existingComment?: Comment;
  onSuccess?: () => void;
  onCancel?: () => void;
  placeholder?: string;
}

export function CommentForm({
  photoId,
  existingComment,
  onSuccess,
  onCancel,
  placeholder = 'Add a comment...',
}: CommentFormProps) {
  const [text, setText] = useState(existingComment?.text || '');
  const createMutation = useCreateComment();
  const updateMutation = useUpdateComment();

  const isEditing = !!existingComment;
  const mutation = isEditing ? updateMutation : createMutation;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!text.trim()) return;

    try {
      if (isEditing && existingComment) {
        await updateMutation.mutateAsync({
          commentId: existingComment.commentId,
          text: text.trim(),
        });
      } else {
        await createMutation.mutateAsync({
          photoId,
          text: text.trim(),
        });
      }

      setText('');
      onSuccess?.();
    } catch (error) {
      console.error('Failed to submit comment:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Cmd+Enter or Ctrl+Enter
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="min-h-[80px] resize-none"
        disabled={mutation.isPending}
        maxLength={500}
      />

      <div className="flex items-center justify-between gap-2">
        <span className="flex-shrink-0 text-xs text-gray-500">{text.length}/500</span>

        <div className="flex flex-shrink-0 items-center gap-2">
          {isEditing && onCancel && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
          )}

          <Button type="submit" size="sm" disabled={!text.trim() || mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? 'Updating...' : 'Posting...'}
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                {isEditing ? 'Update' : 'Post'}
              </>
            )}
          </Button>
        </div>
      </div>

      <p className="text-xs text-gray-500">Tip: Press Cmd+Enter (or Ctrl+Enter) to submit</p>
    </form>
  );
}
